import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { FxAlert, FxButton, FxIcon, FxUploadDialog } from '@/components/fx'
import { PlanQuota } from '@/components/PlanQuota'
import { useUsage } from '@/hooks/useUsage'
import { documentsApi } from '@/lib/api'

import { UploadFileList } from './UploadFileList'
import { useUploadQueue } from './useUploadQueue'
import {
  DEFAULT_MAX_UPLOAD_MB,
  UPLOAD_ACCEPT,
  UPLOAD_FORMATS_LABEL,
  queueIsLive,
  queueSummary,
  refusalNotice,
} from './uploadQueue'

/**
 * "Dokumente hinzufügen" — the scans of filled-in forms, added to one
 * template.
 *
 * A shared component rather than a page: it is opened from the cockpit
 * header, from the app shell's "+" (which sets `?dialog=upload` on whatever
 * route is open) and from the empty state of the document list. Whoever
 * opens it owns the `open`/`onClose` wiring; `useUploadDialogParam` below is
 * the reader for screens that are not the cockpit.
 *
 * What it is careful about:
 *  - one upload at a time (see `useUploadQueue`);
 *  - a queue is never lost silently — closing mid-upload asks first, and an
 *    account-wide refusal holds the waiting files instead of dropping them;
 *  - it states only limits the handler enforces (jpg/png/pdf, the body cap),
 *    not the artboard's;
 *  - a PDF is rasterised **after** the upload, so "hochgeladen" is not
 *    "fertig" and the dialog says so.
 */
export interface AddDocumentsDialogProps {
  templateId: string
  /** For the subtitle. Omitted while the template is still loading. */
  templateTitle?: string
  /** Controlled: render nothing when false. Defaults to open. */
  open?: boolean
  onClose: () => void
  /** Every id that landed, when the dialog closes. Ids of PDFs are included
   *  but are short-lived — see `queueSummary.analysableIds`. */
  onUploaded?: (documentIds: string[]) => void
  /** Override for a deployment with a different `limits.upload_max_size_mb`. */
  maxSizeMb?: number
}

export function AddDocumentsDialog({
  templateId,
  templateTitle,
  open = true,
  onClose,
  onUploaded,
  maxSizeMb = DEFAULT_MAX_UPLOAD_MB,
}: AddDocumentsDialogProps) {
  const queryClient = useQueryClient()
  const usage = useUsage()
  const [analyseAfter, setAnalyseAfter] = useState(true)
  const [confirmingClose, setConfirmingClose] = useState(false)
  const [analysis, setAnalysis] = useState<'idle' | 'started' | 'failed'>('idle')
  const analysed = useRef(new Set<string>())

  /**
   * One invalidation per landed file would be a request per file. The counts,
   * the document list and the quota are all stale once anything lands, so
   * they are refreshed when the queue drains. The key prefixes are the ones
   * the cockpit contract documents (`['documents', 'counts', id]`,
   * `['documents', 'list', id, view]`, `['usage']`); this component deals in
   * the prefixes so it need not import the cockpit's module.
   */
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
    void queryClient.invalidateQueries({ queryKey: ['usage'] })
    void queryClient.invalidateQueries({ queryKey: ['jobs'] })
  }, [queryClient])

  const queue = useUploadQueue({
    templateId,
    maxSizeMb,
    onDrained: refresh,
  })

  const summary = useMemo(() => queueSummary(queue.items), [queue.items])
  const live = queueIsLive(queue.items)

  /**
   * Start the analysis for what landed, once nothing is in flight.
   *
   * **Departure from both the artboard and Flutter**: neither starts one.
   * The gateway does not queue an analysis on upload either, so today a
   * person uploads thirty scans, watches "hochgeladen" thirty times and then
   * has to find them in the list and press "Erneut analysieren" — the
   * product's one job, left undone. The checkbox is in plain sight before
   * anything is sent, and it is an ordinary analysis, not a recognition arm,
   * so no `apply` question arises.
   *
   * Images only: a PDF's document id is a placeholder the converter deletes
   * once it has rendered the pages (`convertDocument`), so a job against it
   * would target a document that no longer exists. Their pages arrive in the
   * list as `neu` and are started from there — which the note below says.
   */
  useEffect(() => {
    if (!analyseAfter || live || queue.blocked) return
    const fresh = summary.analysableIds.filter((id) => !analysed.current.has(id))
    if (fresh.length === 0) return
    fresh.forEach((id) => analysed.current.add(id))
    documentsApi
      .startAnalysis(templateId, fresh)
      .then(() => {
        setAnalysis('started')
        refresh()
      })
      .catch(() => {
        // The scans are stored either way; only the job did not start.
        fresh.forEach((id) => analysed.current.delete(id))
        setAnalysis('failed')
      })
  }, [analyseAfter, live, queue.blocked, refresh, summary.analysableIds, templateId])

  const finish = useCallback(() => {
    onUploaded?.(summary.documentIds)
    onClose()
  }, [onClose, onUploaded, summary.documentIds])

  const requestClose = useCallback(() => {
    // Escape, the backdrop and "Abbrechen" all land here. A queue still in
    // flight is somebody's work: ask rather than drop it.
    if (live) {
      setConfirmingClose(true)
      return
    }
    finish()
  }, [finish, live])

  useEffect(() => {
    if (!live) setConfirmingClose(false)
  }, [live])

  if (!open) return null

  const notice = queue.blocked ? refusalNotice(queue.blocked) : null
  const retentionDays = usage.data?.enforced ? usage.data.usage?.retentionDays : undefined

  return (
    <FxUploadDialog
      title="Dokumente hinzufügen"
      subtitle={
        <>
          {templateTitle ? (
            <>
              zu <b>{templateTitle}</b> ·{' '}
            </>
          ) : null}
          ausgefüllte Formulare, eine Seite pro Bild
        </>
      }
      accept={UPLOAD_ACCEPT}
      multiple
      formats={UPLOAD_FORMATS_LABEL}
      maxSizeLabel={`Maximalgröße: ${maxSizeMb} MB`}
      onFilesSelected={queue.add}
      onClose={requestClose}
      footer={
        <Footer
          confirming={confirmingClose}
          live={live}
          summaryLine={summaryLine(summary, analysis, live)}
          onKeepUploading={() => setConfirmingClose(false)}
          onDiscard={finish}
          onCancel={requestClose}
          onFinish={finish}
          canFinish={summary.done > 0}
        />
      }
    >
      <PlanQuota kind="pages" />

      {notice && (
        <FxAlert tone={notice.tone} title={notice.title}>
          <p className="m-0">{notice.body}</p>
          {summary.blocked > 0 && (
            <p className="m-0 mt-2">
              {summary.blocked === 1
                ? '1 Datei wartet noch und wurde nicht gesendet.'
                : `${summary.blocked} Dateien warten noch und wurden nicht gesendet.`}{' '}
              <button
                type="button"
                onClick={queue.resume}
                className="rounded-sm text-body-strong text-primary-text underline"
              >
                Erneut senden
              </button>
            </p>
          )}
        </FxAlert>
      )}

      <UploadFileList items={queue.items} onRemove={queue.remove} onRetry={queue.retry} />

      {summary.pdfCount > 0 && (
        <p className="m-0 flex items-start gap-2 text-small text-text-muted">
          <FxIcon name="picture_as_pdf" size={20} className="shrink-0 text-primary-strong" />
          <span>
            PDFs werden nach dem Hochladen in einzelne Seiten umgewandelt. Das dauert einen Moment —
            die Seiten erscheinen danach einzeln in der Liste und werden dort analysiert.
          </span>
        </p>
      )}

      <label className="flex items-start gap-3 text-body">
        <input
          type="checkbox"
          checked={analyseAfter}
          onChange={(event) => setAnalyseAfter(event.target.checked)}
          className="mt-1 h-5 w-5 shrink-0 accent-primary-strong"
        />
        <span>
          Analyse direkt starten
          <span className="block text-small text-text-muted">
            Die hochgeladenen Bilder werden sofort ausgewertet. Ohne Haken bleiben sie als „noch
            nicht verarbeitet“ liegen, bis Sie die Analyse in der Liste starten.
          </span>
        </span>
      </label>

      <p className="m-0 text-small text-text-muted">
        Die Dateien werden zur Auswertung verarbeitet und in Ihrem Konto gespeichert.
        {retentionDays ? ` Aufbewahrung: ${retentionDays} Tage.` : ''}
      </p>
    </FxUploadDialog>
  )
}

function summaryLine(
  summary: ReturnType<typeof queueSummary>,
  analysis: 'idle' | 'started' | 'failed',
  live: boolean,
): string {
  if (summary.total === 0) return ''
  const parts = [`${summary.done} von ${summary.total} hochgeladen`]
  if (summary.failed > 0) parts.push(`${summary.failed} fehlgeschlagen`)
  if (summary.rejected > 0) parts.push(`${summary.rejected} nicht möglich`)
  if (!live && analysis === 'started') parts.push('Analyse gestartet')
  if (!live && analysis === 'failed') parts.push('Analyse konnte nicht gestartet werden')
  return parts.join(' · ')
}

function Footer({
  confirming,
  live,
  summaryLine: line,
  onKeepUploading,
  onDiscard,
  onCancel,
  onFinish,
  canFinish,
}: {
  confirming: boolean
  live: boolean
  summaryLine: string
  onKeepUploading: () => void
  onDiscard: () => void
  onCancel: () => void
  onFinish: () => void
  canFinish: boolean
}) {
  if (confirming) {
    return (
      <div className="flex flex-wrap items-center gap-4">
        <span className="flex-1 text-small" role="alert">
          Es werden noch Dateien hochgeladen. Wenn Sie jetzt schließen, werden sie nicht gesendet.
        </span>
        <FxButton variant="tonal" onClick={onKeepUploading}>
          Weiter hochladen
        </FxButton>
        <FxButton variant="dangerTonal" onClick={onDiscard}>
          Trotzdem schließen
        </FxButton>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <span className="flex-1 text-small" aria-live="polite">
        {line}
      </span>
      <FxButton variant="tonal" onClick={onCancel}>
        Abbrechen
      </FxButton>
      <FxButton variant="primary" disabled={live || !canFinish} onClick={onFinish}>
        Fertig
      </FxButton>
    </div>
  )
}
